import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Settings } from "@hub/common/Settings";
import { AngularPrivateApiSettingsDto, AngularPublicApiSettingsDto } from "@hub/generated/models";
import { SessionStorageService } from "@hub/services/SessionStorageService";
import { BehaviorSubject } from "rxjs";

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private static appConfig: BehaviorSubject<AngularPublicApiSettingsDto> = new BehaviorSubject({
    UseMockUser: false,
    AzureTenant: "",
    AzureClientId: "",
    AzureAudience: "",
    AllowCustomLogin: false,
    ConnectUrl: "",
  });

  private static privateAppConfig: BehaviorSubject<
    AngularPrivateApiSettingsDto
  > = new BehaviorSubject({
    AgGridKey: "",
    GoogleApiKey: ""
  });

  constructor(private http: HttpClient, private storageService: SessionStorageService) {
  }

  private getConfigFromApi<T>(url: string, params = {}): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.http.get<T>(url, params).subscribe(resolve, reject);
    });
  }

  loadPublicAppConfig() {
    const params = {};

    if (!!this.storageService.get("apiSettings")) {
      return Promise.resolve(this.storageService.get("apiSettings") as AngularPublicApiSettingsDto).then(data => {
        AppConfigService.appConfig.next(data);
      })
      .catch(() => undefined);
    } else {
      const url = Settings.createApiUrl("Public/GetApiSettings");

      return this.getConfigFromApi<AngularPublicApiSettingsDto>(url, params)
        .then(data => {
          this.storageService.set("apiSettings", data);
          AppConfigService.appConfig.next(data);
        })
        .catch(() => undefined);
    }
  }

  loadPrivateAppConfig() {
    const params = {};

    if (!!this.storageService.get("apiKeys")) {
      return Promise.resolve(this.storageService.get("apiKeys") as AngularPrivateApiSettingsDto).then(data => {
        AppConfigService.privateAppConfig.next(data);
      });
    } else {
      return this.getConfigFromApi<AngularPrivateApiSettingsDto>(
        Settings.createApiUrl("Reference/GetPrivateSettings"),
        params
      )
        .then(data => {
          this.storageService.set("apiKeys", data);
          AppConfigService.privateAppConfig.next(data);
        });
    }
  }

  getConfig() {
    return AppConfigService.appConfig.getValue();
  }

  getConfigAsObservable() {
    return AppConfigService.appConfig.asObservable();
  }

  public static getUseMockUserSt() {
    try {
      return AppConfigService.appConfig.getValue().UseMockUser;
    } catch (error) {
      return false;
    }
  }

  getUseMockUser() {
    try {
      return AppConfigService.appConfig.getValue().UseMockUser;
    } catch (error) {
      return false;
    }
  }

  getConnectUrl() {
    try {
      return AppConfigService.appConfig.getValue().ConnectUrl;
    }
    catch (error){
      return false;
    }
  }

  getAzureTenant() {
    return AppConfigService.appConfig.getValue().AzureTenant;
  }

  getAzureClientId() {
    return AppConfigService.appConfig.getValue().AzureClientId;
  }

  getAzureAudience() {
    return AppConfigService.appConfig.getValue().AzureAudience;
  }

  getCustomLogin() {
    return AppConfigService.appConfig.getValue().AllowCustomLogin;
  }

  getGoogleApiKey() {
    return AppConfigService.privateAppConfig.getValue().GoogleApiKey;
  }

  getAgGridKey() {
    return AppConfigService.privateAppConfig.getValue().AgGridKey;
  }

}
